import axiosInstance from '../axios.config';
import { apiError } from '../apiError';
import { API_ENDPOINTS } from '../endpoints';
import { CallLog, PaginatedResponse, FilterParams, ApiResponse } from 'types';
import { mockCalls } from 'mocks/mockServer';
import { dateStringToEpoch, dateStringToEpochEndOfDay } from 'utils/helpers';

const useMock = process.env.REACT_APP_USE_MOCK === 'true';

console.log('📞 Calls Service initialized - Using Mock:', useMock, 'ENV:', process.env.REACT_APP_USE_MOCK);

/** This organisation's own call totals — see CallLogController#summary. */
export interface CallSummary {
  totalCalls: number;
  inbound: number;
  outbound: number;
  answered: number;
  missed: number;
  durationSeconds: number;
  avgDurationSeconds: number;
  dailySeries: Array<Record<string, any>>;
  partial?: boolean;
  /** Present when there is nowhere to look, as opposed to nothing to show. */
  note?: string;
}

export const callsService = {
  /**
   * Totals for the Overview tiles. Scoped by switch identity on the server, so
   * a business sees its own calls and not its provider's whole range.
   */
  async getSummary(params?: { organizationId?: string; dateFrom?: string; dateTo?: string }):
      Promise<ApiResponse<CallSummary>> {
    try {
      const res = await axiosInstance.get('/calls/summary', { params });
      return res.data;
    } catch (e: any) {
      return apiError(e, 'Failed to load call summary') as any;
    }
  },

  async getCallLogs(params?: FilterParams & { organizationId?: string }): Promise<ApiResponse<PaginatedResponse<CallLog>>> {
    if (useMock) {
      console.log('📞 Using MOCK data for call logs');
      return mockCalls.getCallLogs(params);
    }

    // Convert date strings to epoch timestamps for API
    const apiParams: any = { ...params };
    if (params?.dateFrom) {
      apiParams.fromDateEpoch = dateStringToEpoch(params.dateFrom);
      delete apiParams.dateFrom;
    }
    if (params?.dateTo) {
      apiParams.toDateEpoch = dateStringToEpochEndOfDay(params.dateTo);
      delete apiParams.dateTo;
    }
    // Convert frontend 1-based page to backend 0-based page
    if (params?.page !== undefined) {
      apiParams.page = Math.max(0, params.page - 1);
    }

    console.log('📞 Fetching call logs from API:', API_ENDPOINTS.CALL_LOGS, 'with params:', apiParams);
    try {
      const response = await axiosInstance.get<ApiResponse<PaginatedResponse<CallLog>>>(
        API_ENDPOINTS.CALL_LOGS,
        { params: apiParams }
      );

      // API returns wrapped response: { success, message, data }
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch call logs'
      };
    }
  },

  async getCallDetail(id: number, organizationId?: string): Promise<ApiResponse<CallLog>> {
    if (useMock) {
      return mockCalls.getCallDetail(id);
    }

    try {
      const response = await axiosInstance.get<ApiResponse<CallLog>>(
        API_ENDPOINTS.CALL_LOG_DETAIL(id),
        { params: { organizationId } }
      );

      // API returns wrapped response: { success, message, data }
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch call details'
      };
    }
  },

  async getCallByUniqueId(uniqueId: string, organizationId?: string): Promise<ApiResponse<CallLog>> {
    if (useMock) {
      return mockCalls.getCallDetail(1); // Mock fallback
    }

    try {
      const response = await axiosInstance.get<ApiResponse<CallLog>>(
        `${API_ENDPOINTS.CALL_LOGS}/by-unique-id/${uniqueId}`,
        { params: { organizationId } }
      );

      // API returns wrapped response: { success, message, data }
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch call by unique ID'
      };
    }
  },

  async exportCallLogs(params?: FilterParams): Promise<Blob> {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.CALL_LOGS}/export`,
      {
        params,
        responseType: 'blob'
      }
    );
    
    return response.data;
  }
};